/**
 * 缴费指引（服务台入口 `/payment/guide`，无报名单 id）
 * -----------------------------------------------------------------------------
 * 服务台宫格入口只带路由、不带 id。此前本页只读 `sessionStorage` 里的本地 id ——
 * 换设备 / 清缓存后本地为空，学员在最需要找到待缴费单的时刻看到空态
 * （评审 2026-10-23 H-14）。现改为**服务端列表为主、本地 id 兜底**：
 *   1. `GET /api/h5/registrations`（第一页 50 条，取 pageSize 上限）筛 `PENDING_PAYMENT`；
 *   2. 服务端没给（极端分页外溢）再按本地最近浏览的 id 逐个查；
 *   3. 两路都空才展示空态；列表请求失败给「失败可重试」，**不得**渲染成空态。
 */

import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRegistration, listMyRegistrations } from '../api/h5';
import { WalletIcon } from '../components/icons';
import { Button, EmptyHint, ErrorBlock, LoadingBlock, PageShell } from '../components/layout';
import { readRecentRegistrations } from '../store/recent';

export default function PaymentGuideEntry() {
  const navigate = useNavigate();

  // 主路：服务端「我的报名单」第一页（pageSize 上限 50）。独立 queryKey，
  // 不与报名记录页的 20 条缓存混用 —— 这里要的是「更大的捞取面」。
  const mine = useQuery({
    queryKey: ['h5', 'my-registrations', 'payment-entry'],
    queryFn: () => listMyRegistrations(1, 50),
    retry: false,
    staleTime: 30 * 1000,
  });

  // 兜底：本地最近浏览的报名单 id。**等服务端列表落地后**才启用，
  // 避免两路同时命中待缴费单时跳转目标来回横跳。
  const ids = readRecentRegistrations();
  const localResults = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['h5', 'registration', id],
      queryFn: () => getRegistration(id),
      retry: false,
      enabled: mine.isSuccess,
    })),
  });

  const remotePending = mine.data?.items.find((item) => item.status === 'PENDING_PAYMENT');
  const localPending = localResults.find(
    (result) => result.data?.status === 'PENDING_PAYMENT',
  )?.data;
  const pending = remotePending ?? localPending;

  useEffect(() => {
    if (pending) navigate(`/payment/${pending.id}`, { replace: true });
  }, [pending, navigate]);

  // 网络失败 ≠ 没有待缴费单：失败给可重试的失败态，绝不渲染成空态（H-15 同源修复）
  if (mine.isError) {
    return (
      <PageShell title="缴费指引" back>
        <ErrorBlock
          message="暂时拿不到你的报名单，请检查网络后重试"
          onRetry={() => mine.refetch()}
        />
      </PageShell>
    );
  }

  const localLoading = mine.isSuccess && ids.length > 0 && localResults.some((r) => r.isPending);
  if (mine.isPending || localLoading) {
    return (
      <PageShell title="缴费指引" back>
        <LoadingBlock text="正在查找待缴费报名单" />
      </PageShell>
    );
  }

  return (
    <PageShell
      title="缴费指引"
      back
      footer={
        <div className="btn-row">
          <Button variant="ghost" onClick={() => navigate('/help/service')}>
            联系客服
          </Button>
          <Button variant="primary" onClick={() => navigate('/registration/list')}>
            查看报名记录
          </Button>
        </div>
      }
    >
      {/* 空态居中原稿构图：图标 + 标题 + 说明，动作放页脚成对出现，不塞进空态里 */}
      <EmptyHint
        icon={<WalletIcon size={34} strokeWidth={1.5} />}
        title="当前没有待缴费的报名单"
        description="提交报名后会生成待缴费单，可在这里查看转账备注码与缴费截止时间"
        fill
      />
    </PageShell>
  );
}
